import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useRef } from 'react';
import { NavMarker } from 'react-rhumb';
import { CRM_SUITE_ENTERPRISE_BUNDLE, CRM_SUITE_PROFESSIONAL_BUNDLE, CRM_SUITE_STARTER_BUNDLE, HUBSPOT_FREE } from 'self-service-api/constants/ApiNames';
import { getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import FeatureTable from 'ui-addon-upgrades/featureTable/FeatureTable';
import { NavItemEnum } from '../../../constants/routing/NavItems';
import { useAppSelector } from '../../../reducers';
import { isUngatedForSeatsSelector } from '../../../selectors/GatingSelectors';
import { sortSkuCategoryNames } from '../../../utils/sortingUtils';
import { CustomError } from '../../../v2/components/error-boundary/CustomErrors';
import { useErrorBoundary } from '../../../v2/components/error-boundary/useErrorBoundary';
import { withErrorBoundary } from '../../../v2/components/error-boundary/withErrorBoundary';
import { productLineToSkuCategoryName, skuCategoryNameToProductLine } from '../../../v2/constants';
import { PinnedFeaturesConfig } from '../../../v2/constants/PinnedFeaturesConfig';
import { useBadges } from '../../../v2/hooks/badges/useBadges';
import { usePinnedFeaturesBySkuCategoryName } from '../../../v2/hooks/features/useFeatures';
import { isValidProductLine } from '../../../v2/utils/typeguards';
import SuiteFeatureTableHeader from './SuiteFeatureTableHeader';
import SuiteFeatureTableHeaderCell from './SuiteFeatureTableHeaderCell';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const matchApiNameByLevel = {
  FREE: HUBSPOT_FREE,
  STARTER: CRM_SUITE_STARTER_BUNDLE,
  PROFESSIONAL: CRM_SUITE_PROFESSIONAL_BUNDLE,
  ENTERPRISE: CRM_SUITE_ENTERPRISE_BUNDLE
};
const buildSuiteFeatureTableHeaderData = (levels, isFreeToStarter = false, screen, badges) => {
  return levels.reduce((headers, level) => {
    var _badges$matchApiNameB;
    headers[matchApiNameByLevel[level]] = {
      Content: /*#__PURE__*/_jsx(SuiteFeatureTableHeaderCell, {
        productLevel: level,
        isFreeToStarter: isFreeToStarter,
        screen: screen,
        badgeName: badges === null || badges === void 0 || (_badges$matchApiNameB = badges[matchApiNameByLevel[level]]) === null || _badges$matchApiNameB === void 0 ? void 0 : _badges$matchApiNameB.badgeName
      }, level)
    };
    return headers;
  }, {});
};
const buildSuiteFeatureTableBodyData = ({
  features,
  subscreen,
  skuCategoryNames,
  apiNames,
  staticDisplayRowCount
}) => {
  return {
    sharedData: {
      subscreen,
      staticDisplayRowCount
    },
    sectionData: skuCategoryNames.reduce((acc, skuCategoryName) => {
      const productLine = skuCategoryNameToProductLine[skuCategoryName];
      acc[skuCategoryName] = {
        apiNames: apiNames[skuCategoryName] || [],
        features: features[skuCategoryName] || [],
        LabelContent: productLine ? /*#__PURE__*/_jsx(SuiteFeatureTableHeader, {
          productLine: productLine
        }) : /*#__PURE__*/_jsx(_Fragment, {})
      };
      return acc;
    }, {})
  };
};
const SuiteFeatureTable = ({
  productsByProductLine,
  isFreeToStarter,
  screen
}) => {
  var _PinnedFeaturesConfig;
  const isSeatsRedesignUngated = useAppSelector(isUngatedForSeatsSelector);
  const {
    showBoundary
  } = useErrorBoundary();
  const productsBySkuCategoryName = Object.keys(productsByProductLine).reduce((acc, productline) => {
    if (!isValidProductLine(productline)) {
      return acc;
    }
    acc[productLineToSkuCategoryName[productline]] = productsByProductLine[productline];
    return acc;
  }, {});
  const {
    featuresBySkuCategoryName,
    loading,
    error
  } = usePinnedFeaturesBySkuCategoryName(productsBySkuCategoryName);
  const {
    badges
  } = useBadges({
    productApiNames: Object.values(matchApiNameByLevel)
  });
  const skuCategoryNames = Object.keys(featuresBySkuCategoryName);
  const featureTableRef = useRef(null);
  const sortedSkuCategoryNames = sortSkuCategoryNames(skuCategoryNames);
  const levels = new Set();
  Object.values(productsByProductLine).forEach(products => {
    products.forEach(product => {
      const level = getProductLevelFromApiNameString(product);
      levels.add(level);
    });
  });
  if (loading) return /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true
  });
  if (error) {
    showBoundary(new CustomError(error.message || 'Error fetching features', {
      error,
      productsByProductLine,
      fingerprint: ['errorFetchingFeatures']
    }));
  }
  if (!featuresBySkuCategoryName || !Object.keys(featuresBySkuCategoryName).length) {
    return null;
  }
  const tableHeader = buildSuiteFeatureTableHeaderData([...levels], isFreeToStarter, screen, badges);
  const tableBody = buildSuiteFeatureTableBodyData({
    features: featuresBySkuCategoryName,
    subscreen: NavItemEnum.SUITE,
    skuCategoryNames: sortedSkuCategoryNames,
    apiNames: productsBySkuCategoryName,
    staticDisplayRowCount: Math.min(((_PinnedFeaturesConfig = PinnedFeaturesConfig['MARKETING_PRODUCTS']) === null || _PinnedFeaturesConfig === void 0 ? void 0 : _PinnedFeaturesConfig.length) || 5, 5)
  });
  if (!tableBody) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: "SUITE_FEATURE_TABLE"
    }), /*#__PURE__*/_jsx("div", {
      ref: featureTableRef,
      "data-selenium": "suite-feature-table",
      id: "feature-table",
      children: /*#__PURE__*/_jsx(FeatureTable, {
        headerData: tableHeader,
        bodyData: tableBody,
        canViewNewSeats: isSeatsRedesignUngated,
        isInbound25PricingPage: true
      })
    })]
  });
};
export default withErrorBoundary(SuiteFeatureTable, {
  fallback: null,
  errorBoundaryName: 'SUITE_FEATURE_TABLE'
});